import { forwardRef } from "react";
import { Link, useLocation } from "react-router-dom";

// material-ui
import { styled, Theme, CSSObject, useTheme } from "@mui/material/styles";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";

// project import
import Logo from "components/logo";
import menu from "menu";
import { DRAWER_WIDTH } from "config";
import { ThemeMode } from "types/config";

// assets
import { nuwaySymbol } from "components/logo/brand";

// ==============================|| MAIN LAYOUT - DRAWER (same shell as front-end) ||============================== //

const openedMixin = (theme: Theme): CSSObject => ({
  width: DRAWER_WIDTH,
  borderRight: `1px solid ${theme.palette.divider}`,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  boxShadow:
    theme.palette.mode === ThemeMode.DARK ? theme.customShadows.z1 : "none",
  backgroundColor: theme.palette.background.paper,
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: theme.spacing(7.5),
  borderRight: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.customShadows.z1,
  backgroundColor: theme.palette.background.paper,
});

const MiniDrawerStyled = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "open",
})<{ open: boolean }>(({ theme, open }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  "& .MuiDrawer-paper": {
    backgroundImage: "none",
    backgroundColor: theme.palette.background.paper,
  },
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

// ==============================|| NAV ITEM ||============================== //

const NavItem = ({
  item,
  open,
  onNavigate,
}: {
  item: (typeof menu)[number];
  open: boolean;
  onNavigate: () => void;
}) => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const isDark = theme.palette.mode === ThemeMode.DARK;
  const isSelected = !!item.url && pathname.startsWith(item.url);
  const Icon = item.icon as any;

  const textColor = isDark ? "grey.400" : "text.primary";
  const iconSelectedColor = isDark && open ? "text.primary" : "primary.main";

  const selectedStyles = {
    bgcolor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    boxShadow: `0 12px 24px ${theme.palette.primary.main}22`,
    "& .MuiListItemIcon-root, & .MuiTypography-root": {
      color: theme.palette.primary.contrastText,
    },
    "&:hover": { bgcolor: theme.palette.primary.dark },
  };

  const LinkBehavior = forwardRef<HTMLAnchorElement, any>((props, ref) => (
    <Link {...props} to={item.url as string} ref={ref} />
  ));

  const button = (
    <ListItemButton
      component={LinkBehavior}
      disabled={item.disabled}
      selected={isSelected}
      onClick={onNavigate}
      sx={{
        zIndex: 1201,
        mb: open ? 0.375 : 0.75,
        minHeight: open ? 40 : 44,
        borderRadius: 2.5,
        px: open ? 1 : 0.5,
        justifyContent: open ? "flex-start" : "center",
        pl: open ? "8px" : 1,
        pr: 1,
        py: 0.625,
        transition: theme.transitions.create(
          ["background-color", "color", "padding"],
          { duration: theme.transitions.duration.shorter }
        ),
        ...(open && {
          color: textColor,
          "&:hover": { bgcolor: isDark ? "divider" : "grey.100" },
          "&.Mui-selected": selectedStyles,
        }),
        ...(!open && {
          bgcolor: "transparent",
          "&:hover": { bgcolor: "transparent" },
          "&.Mui-selected": {
            bgcolor: "transparent",
            "&:hover": { bgcolor: "transparent" },
          },
        }),
      }}
    >
      {Icon && (
        <ListItemIcon
          sx={{
            minWidth: open ? 24 : 40,
            mr: open ? 0.625 : 0,
            color:
              isSelected && open
                ? theme.palette.primary.contrastText
                : isSelected
                ? iconSelectedColor
                : textColor,
            ...(!open && {
              borderRadius: 2,
              width: 40,
              height: 40,
              border: "1px solid",
              borderColor: isSelected
                ? isDark
                  ? theme.palette.primary.main
                  : theme.palette.primary.light
                : isDark
                ? theme.palette.divider
                : theme.palette.grey[200],
              bgcolor: isSelected
                ? isDark
                  ? "primary.900"
                  : "primary.lighter"
                : isDark
                ? "background.paper"
                : "#fff",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: isDark ? "none" : "0 2px 10px rgba(15, 23, 42, 0.04)",
              "&:hover": { bgcolor: isDark ? "secondary.light" : "grey.50" },
            }),
          }}
        >
          <Icon style={{ fontSize: open ? "1rem" : "1.25rem" }} />
        </ListItemIcon>
      )}
      {open && (
        <ListItemText
          primary={
            <Typography
              variant="h6"
              sx={{
                color: isSelected
                  ? theme.palette.primary.contrastText
                  : textColor,
                fontSize: "0.875rem",
                lineHeight: 1.25,
                fontWeight: 500,
                letterSpacing: 0,
              }}
            >
              {item.title}
            </Typography>
          }
          sx={{
            my: 0,
            flex: "0 1 auto",
            "& .MuiTypography-root": {
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }}
        />
      )}
    </ListItemButton>
  );

  return open ? (
    button
  ) : (
    <Tooltip title={item.title} placement="right">
      <span>{button}</span>
    </Tooltip>
  );
};

// ==============================|| DRAWER ||============================== //

interface Props {
  open: boolean;
  onToggle: () => void;
}

const AdminDrawer = ({ open, onToggle }: Props) => {
  const theme = useTheme();
  const downLG = useMediaQuery(theme.breakpoints.down("lg"));

  // On small screens the drawer is always a full-width temporary sheet.
  const expanded = downLG ? true : open;

  const header = (
    <Box
      sx={{
        ...theme.mixins.toolbar,
        display: "flex",
        alignItems: "center",
        justifyContent: expanded ? "flex-start" : "center",
        minHeight: "64px",
        pt: "16px",
        pb: "4px",
        pl: expanded ? "16px" : 0,
        pr: expanded ? "16px" : 0,
      }}
    >
      <Logo
        isIcon={!expanded}
        sx={{ width: expanded ? "100%" : 35, height: 35 }}
      />
    </Box>
  );

  const content = (
    <Box
      sx={{
        px: 0.75,
        pb: 1.25,
        pt: 0.5,
        overflowY: "auto",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: expanded ? "block" : "none",
          pl: 1.25,
          pb: 1,
          pt: 1,
          fontWeight: 600,
          letterSpacing: ".04em",
          textTransform: "uppercase",
        }}
      >
        Admin
      </Typography>
      <List disablePadding>
        {menu.map((item) => (
          <NavItem
            key={item.id}
            item={item}
            open={expanded}
            onNavigate={() => downLG && onToggle()}
          />
        ))}
      </List>
    </Box>
  );

  const poweredBy = (
    <Box
      sx={{
        mt: "auto",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: expanded ? "0 16px 8px" : "10px 8px 8px",
        fontSize: 12,
        color: "text.secondary",
      }}
    >
      {expanded && <span style={{ marginRight: 5 }}>Powered by</span>}
      <a
        href="https://nuway.co"
        target="_blank"
        rel="noreferrer"
        style={{ display: "flex" }}
      >
        <img
          style={{ width: 20 }}
          src={nuwaySymbol(theme.palette.mode === ThemeMode.DARK)}
          alt="Nuway"
        />
      </a>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ flexShrink: { md: 0 }, zIndex: 1200 }}
      aria-label="admin navigation"
    >
      {!downLG ? (
        <MiniDrawerStyled variant="permanent" open={open}>
          <Box
            sx={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            {header}
            {content}
            {poweredBy}
          </Box>
        </MiniDrawerStyled>
      ) : (
        <Drawer
          variant="temporary"
          open={open}
          onClose={onToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", lg: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
              borderRight: `1px solid ${theme.palette.divider}`,
              backgroundImage: "none",
              boxShadow: "inherit",
              overflow: "hidden",
            },
          }}
        >
          <Box
            sx={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            {header}
            {content}
            {poweredBy}
          </Box>
        </Drawer>
      )}
    </Box>
  );
};

export default AdminDrawer;
